import { createSelector } from '@reduxjs/toolkit';
import { cloneDeep } from 'es-toolkit';

import api from '@/redux/api/searchSuggestions';
import { AppDispatch, AppGetState, GlobalState } from '@/redux/store';

import { getClientIpAddress } from './browser';
import { getDeployment } from './config';

export const SEARCH_SUGGESTIONS_FAIL = 'la/ui/searchSuggestions/FAIL';
export const SEARCH_SUGGESTIONS_REQUEST = 'la/ui/searchSuggestions/REQUEST';
export const SEARCH_SUGGESTIONS_SUCCESS = 'la/ui/searchSuggestions/SUCCESS';

export type SearchSuggestion = {
    houseId?: number;
    label: string;
    value: string;
};

type RelatedSuggestions = {
    suggestions: SearchSuggestion[];
    title: string;
};

// reducer
export type SearchSuggestionsSlice = {
    error: boolean;
    secondarySuggestions: SearchSuggestion[];
    submitted: boolean;
    success: boolean;
    suggestions: SearchSuggestion[];
};

export const defaultSearchSuggestionsSlice: SearchSuggestionsSlice = {
    error: false,
    secondarySuggestions: [],
    submitted: false,
    success: false,
    suggestions: [],
};

export type State = typeof defaultSearchSuggestionsSlice;

export default function reducer(state = defaultSearchSuggestionsSlice, action: any = {}): SearchSuggestionsSlice {
    switch (action.type) {
        case SEARCH_SUGGESTIONS_REQUEST:
            return {
                ...state,
                submitted: true,
                success: false,
            };
        case SEARCH_SUGGESTIONS_SUCCESS:
            if (action.meta.secondary) {
                return {
                    ...state,
                    secondarySuggestions: cloneDeep(action.payload),
                    submitted: false,
                    success: true,
                };
            }

            return {
                ...state,
                submitted: false,
                success: true,
                suggestions: cloneDeep(action.payload),
            };
        case SEARCH_SUGGESTIONS_FAIL:
            return {
                ...state,
                error: true,
                submitted: false,
                success: false,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state;
const searchSuggestionsSlice = createSelector(stateSelector, (state) => state.searchSuggestions);

export const getSecondarySearchSuggestions = createSelector(searchSuggestionsSlice, (uiSearchSuggestions) => {
    if (uiSearchSuggestions.secondarySuggestions) {
        // Use Maps to track unique items and houses
        const uniqueHousesMap = new Map<string, SearchSuggestion>();
        const uniqueSuggestionsMap = new Map<string, SearchSuggestion>();

        uiSearchSuggestions.secondarySuggestions.forEach((result) => {
            if (result.label === 'Auction Houses') {
                return;
            }

            // Create a unique key using houseId, label, and value
            const uniqueKey = `${result.houseId || 0}-${result.label}-${result.value}`;

            if (result.houseId) {
                uniqueHousesMap.set(uniqueKey, result);
            } else {
                uniqueSuggestionsMap.set(uniqueKey, result);
            }
        });

        return {
            houses: Array.from(uniqueHousesMap.values()),
            suggestions: Array.from(uniqueSuggestionsMap.values()),
        };
    }
    return { houses: [], suggestions: [] };
});

export const getSearchSuggestions = createSelector(
    searchSuggestionsSlice,
    (uiSearchSuggestions): RelatedSuggestions[] => {
        if (uiSearchSuggestions.suggestions) {
            // Use Maps to track unique items and houses
            const uniqueHousesMap = new Map<string, SearchSuggestion>();
            const uniqueItemsMap = new Map<string, SearchSuggestion>();

            uiSearchSuggestions.suggestions.forEach((result) => {
                if (result.label === 'Auction Houses') {
                    return;
                }

                // Create a unique key using houseId, label, and value
                const uniqueKey = `${result.houseId || 0}-${result.label}-${result.value}`;

                if (result.houseId) {
                    uniqueHousesMap.set(uniqueKey, result);
                } else {
                    uniqueItemsMap.set(uniqueKey, result);
                }
            });

            let suggestions: RelatedSuggestions[] = [
                {
                    suggestions: Array.from(uniqueItemsMap.values()),
                    title: '',
                },
            ];

            if (uniqueHousesMap.size > 0) {
                suggestions.push({
                    suggestions: Array.from(uniqueHousesMap.values()),
                    title: 'Auction Houses',
                });
            }
            return suggestions || [{ suggestions: [], title: '' }];
        } else {
            return [{ suggestions: [], title: '' }];
        }
    }
);

/* ACTION CREATORS */
export const fetchSearchSuggestions =
    (term: string, secondary: boolean = false, useScoutCategories?: boolean) =>
    async (dispatch: AppDispatch, getState: AppGetState) => {
        try {
            const state = getState();
            const deployment = getDeployment(state);
            const ipAddress = getClientIpAddress(state);

            dispatch({
                payload: term,
                type: SEARCH_SUGGESTIONS_REQUEST,
            });
            const response = await api.fetchSuggestions({
                deployment,
                ipAddress,
                term,
                useScoutCategories,
            });
            dispatch({
                meta: { secondary },
                payload: response.payload,
                type: SEARCH_SUGGESTIONS_SUCCESS,
            });
        } catch (error) {
            dispatch({
                error: true,
                meta: { term },
                payload: error,
                type: SEARCH_SUGGESTIONS_FAIL,
            });
        }
    };
